import React from 'react';
import { Box, Typography, Grid, Paper, Avatar, Chip, Stack } from '@mui/material';
import {
    WbTwilight as LifeRhythmIcon,
    CleaningServices as CleanlinessIcon,
    Chat as CommunicationIcon,
    HomeWork as StayingHomeIcon,
    People as VisitorsIcon,
    SmokingRooms as SmokingIcon,
    Pets as PetsIcon,
    VerifiedUser as VerifiedIcon,
    Email as EmailIcon,
    Phone as PhoneIcon
} from '@mui/icons-material';

// 入居希望時期の日本語変換マップ
const MOVE_IN_PREFERENCE_MAP = {
    'within 1 month': '1ヶ月以内',
    'within 3 months': '3ヶ月以内',
    'within 6 months': '6ヶ月以内',
    'more than 6 months': '6ヶ月以上'
};

// 居住希望期間の日本語変換マップ
const STAY_DURATION_MAP = {
    'short': '短期（1-3ヶ月）',
    'medium': '中期（3-6ヶ月）',
    'long': '長期（6ヶ月以上）'
};

// マッチング条件の日本語変換マップ
const MATCHING_PREFERENCES_MAP = {
    smoking: {
        label: '喫煙',
        icon: SmokingIcon,
        'non-smoker': '喫煙しない',
        'smoker': '喫煙する'
    },
    pets: {
        label: 'ペット',
        icon: PetsIcon,
        'no': 'ペットを飼っていない',
        'yes': 'ペットを飼っている'
    },
    lifeRhythm: {
        label: '生活リズム',
        icon: LifeRhythmIcon,
        morning: '朝型',
        night: '夜型'
    },
    cleanliness: {
        label: '清潔さ',
        icon: CleanlinessIcon,
        clean: 'きれい好き',
        relaxed: 'ある程度の散らかりはOK'
    },
    communication: {
        label: 'コミュニケーション',
        icon: CommunicationIcon,
        active: '積極的に交流',
        minimal: '必要最低限の交流'
    },
    stayingHome: {
        label: '在宅状況',
        icon: StayingHomeIcon,
        often: '在宅が多い',
        rarely: '外出が多い'
    },
    visitors: {
        label: '来客',
        icon: VisitorsIcon,
        welcome: '友人を招くことがある',
        rarely: 'ほとんど招かない'
    }
};

function ProfileView({ user }) {
    const profile = user.profile || {};
    const { preferences } = profile;
    const matchingPreferences = profile.matchingPreferences || {
        smoking: 'non-smoker',
        pets: 'no',
        lifeRhythm: 'morning',
        cleanliness: 'clean',
        communication: 'active',
        stayingHome: 'often',
        visitors: 'welcome'
    };
    const rentRange = preferences.rentRange || { min: 0, max: 0 };
    const lifestyle = preferences.lifestyle || { smoking: false, pets: false };
    const preferredArea = preferences.preferredAreas?.[0] || {};

    // 年齢計算
    const age = profile.dateOfBirth ?
        Math.floor((new Date() - new Date(profile.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000)) :
        null;

    // 希望エリアのテキスト生成
    const getAreaText = () => {
        const parts = [];
        if (preferredArea.prefecture) parts.push(preferredArea.prefecture);
        if (preferredArea.city) parts.push(preferredArea.city);
        if (preferredArea.line) parts.push(preferredArea.line);
        if (preferredArea.station) parts.push(`${preferredArea.station}駅`);
        return parts.length > 0 ? parts.join(' ') : '未設定';
    };

    // マッチング条件の表示内容を取得
    const getMatchingPreferenceValue = (key) => {
        const value = matchingPreferences[key];
        if (!value) return null;
        return MATCHING_PREFERENCES_MAP[key][value];
    };

    // マッチング条件が設定されているか確認
    const hasMatchingPreferences = Object.values(matchingPreferences).some(value => value !== null);

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                    src={profile.profileImage}
                    sx={{ width: 120, height: 120, mr: 3 }}
                />
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="h5" sx={{ mr: 1 }}>{profile.nickname || '名前未設定'}</Typography>
                        {profile.sameGenderOnly && (
                            <Chip
                                color="secondary"
                                label="同性のみ募集"
                                size="small"
                            />
                        )}
                    </Box>
                    <Typography color="textSecondary">
                        {age ? `${age}歳` : ''}
                        {profile.gender ? `・${profile.gender === 'male' ? '男性' : '女性'}` : ''}
                    </Typography>
                    <Typography>{profile.occupation || ''}</Typography>

                    {/* 認証バッジ */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        {user.emailVerified && (
                            <Chip
                                icon={<EmailIcon />}
                                label="メール認証済み"
                                color="success"
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 2 }}
                            />
                        )}
                        {user.phoneVerified && (
                            <Chip
                                icon={<PhoneIcon />}
                                label="電話番号認証済み"
                                color="success"
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 2 }}
                            />
                        )}
                    </Box>
                </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>自己紹介</Typography>
                <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>{profile.introduction || '自己紹介文が未設定です'}</Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>希望条件</Typography>
                    <Box>
                        <Typography variant="subtitle2">希望家賃</Typography>
                        <Typography>
                            {rentRange.min || rentRange.max ?
                                `¥${rentRange.min.toLocaleString()} ～ ¥${rentRange.max.toLocaleString()}` :
                                '未設定'}
                        </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">希望エリア</Typography>
                        <Typography>
                            {getAreaText()}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>ライフスタイル</Typography>

                    {/* 喫煙 */}
                    {matchingPreferences.smoking && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>喫煙</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                    icon={<SmokingIcon />}
                                    label="喫煙しない"
                                    color={matchingPreferences.smoking === 'non-smoker' ? "primary" : "default"}
                                    variant={matchingPreferences.smoking === 'non-smoker' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                                <Chip
                                    icon={<SmokingIcon />}
                                    label="喫煙する"
                                    color={matchingPreferences.smoking === 'smoker' ? "primary" : "default"}
                                    variant={matchingPreferences.smoking === 'smoker' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* ペット */}
                    {matchingPreferences.pets && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>ペット</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                    icon={<PetsIcon />}
                                    label="ペットを飼っていない"
                                    color={matchingPreferences.pets === 'no' ? "primary" : "default"}
                                    variant={matchingPreferences.pets === 'no' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                                <Chip
                                    icon={<PetsIcon />}
                                    label="ペットを飼っている"
                                    color={matchingPreferences.pets === 'yes' ? "primary" : "default"}
                                    variant={matchingPreferences.pets === 'yes' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* マッチング条件 */}
                    {matchingPreferences.lifeRhythm && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>生活リズム</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                    icon={<LifeRhythmIcon />}
                                    label="朝型"
                                    color={matchingPreferences.lifeRhythm === 'morning' ? "primary" : "default"}
                                    variant={matchingPreferences.lifeRhythm === 'morning' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                                <Chip
                                    icon={<LifeRhythmIcon />}
                                    label="夜型"
                                    color={matchingPreferences.lifeRhythm === 'night' ? "primary" : "default"}
                                    variant={matchingPreferences.lifeRhythm === 'night' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                            </Box>
                        </Box>
                    )}

                    {matchingPreferences.cleanliness && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>清潔さ</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<CleanlinessIcon />}
                                    label="きれい好き"
                                    color={matchingPreferences.cleanliness === 'clean' ? "primary" : "default"}
                                    variant={matchingPreferences.cleanliness === 'clean' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                                <Chip
                                    icon={<CleanlinessIcon />}
                                    label="ある程度の散らかりはOK"
                                    color={matchingPreferences.cleanliness === 'relaxed' ? "primary" : "default"}
                                    variant={matchingPreferences.cleanliness === 'relaxed' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                            </Box>
                        </Box>
                    )}

                    {matchingPreferences.communication && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>コミュニケーション</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<CommunicationIcon />}
                                    label="積極的に交流"
                                    color={matchingPreferences.communication === 'active' ? "primary" : "default"}
                                    variant={matchingPreferences.communication === 'active' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                                <Chip
                                    icon={<CommunicationIcon />}
                                    label="必要最低限の交流"
                                    color={matchingPreferences.communication === 'minimal' ? "primary" : "default"}
                                    variant={matchingPreferences.communication === 'minimal' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                            </Box>
                        </Box>
                    )}

                    {matchingPreferences.stayingHome && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>在宅状況</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                    icon={<StayingHomeIcon />}
                                    label="在宅が多い"
                                    color={matchingPreferences.stayingHome === 'often' ? "primary" : "default"}
                                    variant={matchingPreferences.stayingHome === 'often' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                                <Chip
                                    icon={<StayingHomeIcon />}
                                    label="外出が多い"
                                    color={matchingPreferences.stayingHome === 'rarely' ? "primary" : "default"}
                                    variant={matchingPreferences.stayingHome === 'rarely' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                            </Box>
                        </Box>
                    )}

                    {matchingPreferences.visitors && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>来客</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<VisitorsIcon />}
                                    label="友人を招くことがある"
                                    color={matchingPreferences.visitors === 'welcome' ? "primary" : "default"}
                                    variant={matchingPreferences.visitors === 'welcome' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                                <Chip
                                    icon={<VisitorsIcon />}
                                    label="ほとんど招かない"
                                    color={matchingPreferences.visitors === 'rarely' ? "primary" : "default"}
                                    variant={matchingPreferences.visitors === 'rarely' ? "filled" : "outlined"}
                                    size="small"
                                    sx={{ borderRadius: 2 }}
                                />
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">入居希望時期</Typography>
                        <Typography>
                            {profile.moveInPreference ?
                                MOVE_IN_PREFERENCE_MAP[profile.moveInPreference] || profile.moveInPreference :
                                '未設定'}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">居住希望期間</Typography>
                        <Typography>
                            {profile.stayDuration ?
                                STAY_DURATION_MAP[profile.stayDuration] || '未設定' :
                                '未設定'}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
}

export default ProfileView; 